import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import CompanyFilterSelect from '../../components/CompanyFilterSelect/CompanyFilterSelect';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import useCompanyFilter from '../../hooks/useCompanyFilter';
import jsPDF from 'jspdf';
import '../../styles/ListPage.css';
import '../../styles/FormPage.css';

const FILTER_OPTIONS = [
    { value: 'All', label: 'All entries (bills & income)' },
    { value: 'Bills', label: 'Bills only' },
    { value: 'Income', label: 'Income only (all types)' },
    { value: 'Cash', label: 'Cash income only' },
    { value: 'Cheque', label: 'Cheque income only' },
    { value: 'Online', label: 'Online income only' },
];

function getBillFinalAmount(bill) {
    const totalGST = parseFloat((bill.gstRate * 2) / 100);
    const totalAmount = parseFloat(bill.totalAmount || bill.amount || 0);
    const gstAmount = parseFloat((totalAmount * totalGST).toFixed(2));
    return parseFloat((totalAmount + gstAmount).toFixed(2));
}

function getRowAmount(item) {
    if (item.entryType === 'Bill' || item.gstRate != null) {
        return getBillFinalAmount(item);
    }
    return parseFloat(item.amount || 0);
}

function formatMoney(value) {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function LedgerDetails() {
    const { clientId } = useParams();
    const [searchParams] = useSearchParams();
    const companyFromUrl = searchParams.get('companyId') ?? undefined;
    const {
        companies,
        selectedCompanyId,
        handleCompanyChange,
        companyQueryParam,
        companiesLoaded,
    } = useCompanyFilter(companyFromUrl);
    const [bills, setBills] = useState([]);
    const [incomeEntries, setIncomeEntries] = useState([]);
    const [combinedData, setCombinedData] = useState([]);
    const [totalBillAmount, setTotalBillAmount] = useState(0);
    const [totalIncomeAmount, setTotalIncomeAmount] = useState(0);
    const [clientDetails, setClientDetails] = useState(null);
    const [ledgerFilter, setLedgerFilter] = useState('All');
    const [draftLedgerFilter, setDraftLedgerFilter] = useState('All');
    const [showFilterModal, setShowFilterModal] = useState(false);
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);

    useEffect(() => {
        const combined = [
            ...bills.map(bill => ({
                ...bill,
                date: bill.invoiceDate,
                entryType: 'Bill',
                amount: bill.totalAmount
            })),
            ...incomeEntries.map(income => ({
                ...income,
                entryType: 'Income'
            }))
        ].sort((a, b) => new Date(a.date) - new Date(b.date));

        setCombinedData(combined);
    }, [bills, incomeEntries]);

    const filteredData = useMemo(() => {
        if (ledgerFilter === 'All') return combinedData;
        if (ledgerFilter === 'Bills') {
            return combinedData.filter(item => item.entryType === 'Bill');
        }
        if (ledgerFilter === 'Income') {
            return combinedData.filter(item => item.entryType === 'Income');
        }
        return combinedData.filter(
            item => item.entryType === 'Income' && item.type === ledgerFilter
        );
    }, [combinedData, ledgerFilter]);

    const fetchBills = useCallback(async () => {
        try {
            const response = await api.get(`/bills/client/${clientId}`, {
                params: companyQueryParam,
            });
            setBills(response.data);
            const total = response.data.reduce((sum, bill) => sum + getBillFinalAmount(bill), 0);
            setTotalBillAmount(total);
        } catch (error) {
            console.error('Error fetching bills:', error);
            alert('Error fetching bills');
        }
    }, [clientId, selectedCompanyId]);

    const fetchIncome = useCallback(async () => {
        try {
            const response = await api.get(`/income/client/${clientId}`, {
                params: companyQueryParam,
            });
            setIncomeEntries(response.data);
            const total = response.data.reduce((sum, entry) => sum + parseFloat(entry.amount), 0);
            setTotalIncomeAmount(total);
        } catch (error) {
            console.error('Error fetching income:', error);
            const message = error.response?.data?.Error || error.message || 'Error fetching income';
            alert(message);
        }
    }, [clientId, selectedCompanyId]);

    const fetchClientDetails = useCallback(async () => {
        try {
            const response = await api.get(`/clients/${clientId}`);
            setClientDetails(response.data);
        } catch (error) {
            alert('Error fetching client details');
        }
    }, [clientId]);

    useEffect(() => {
        if (!companiesLoaded) {
            return;
        }

        withLoading(async () => {
            await fetchBills();
            await fetchIncome();
            await fetchClientDetails();
        });
    }, [clientId, fetchBills, fetchIncome, fetchClientDetails, withLoading, companiesLoaded]);

    const pendingAmount = totalBillAmount - totalIncomeAmount;

    const openFilterModal = () => {
        setDraftLedgerFilter(ledgerFilter);
        setShowFilterModal(true);
    };

    const applyFilter = () => {
        setLedgerFilter(draftLedgerFilter);
        setShowFilterModal(false);
    };

    const clearFilter = () => {
        setDraftLedgerFilter('All');
        setLedgerFilter('All');
        setShowFilterModal(false);
    };

    const filterLabel = FILTER_OPTIONS.find(option => option.value === ledgerFilter)?.label
        || 'All entries (bills & income)';

    const generatePDF = () => {
        if (!clientDetails) return;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;

        const img = new Image();
        img.src = process.env.PUBLIC_URL + '/assets/companyLogo.png';

        const logoWidth = 80;
        const logoHeight = 35;
        const xPosition = (pageWidth - logoWidth) / 2;
        doc.addImage(img, 'PNG', xPosition, 0, logoWidth, logoHeight);

        doc.setDrawColor(0);
        doc.setLineWidth(0.5);
        doc.line(15, 35, pageWidth - 15, 35);

        doc.setFontSize(14);
        doc.text(`Ledger Details - ${clientDetails.name}`, 15, 45);

        if (ledgerFilter !== 'All') {
            doc.setFontSize(10);
            doc.text(`Filter: ${filterLabel}`, 15, 52);
        }

        doc.setFontSize(12);
        let y = ledgerFilter !== 'All' ? 65 : 60;

        doc.text('Date', 15, y);
        doc.text('Description', 50, y);
        doc.text('Type', 110, y);
        doc.text('Amount', 160, y);

        doc.line(15, y + 2, pageWidth - 15, y + 2);

        y += 10;

        filteredData.forEach((item) => {
            if (y > 270) {
                doc.addPage();
                y = 20;
            }

            doc.text(formatDateDDMMYYYY(item.date), 15, y);
            doc.text(
                item.entryType === 'Bill' ? 'Bill' : (item.description || 'Income'),
                50,
                y
            );
            doc.text(item.entryType === 'Bill' ? (item.invoiceNo || '-') : (item.type || '-'), 110, y);
            doc.text(getRowAmount(item).toString(), 180, y, { align: 'right' });

            y += 7;
        });

        y += 5;
        doc.line(15, y, pageWidth - 15, y);
        y += 7;
        doc.text(`Total Bills: ${totalBillAmount}`, 15, y);
        y += 7;
        doc.text(`Total Income: ${totalIncomeAmount}`, 15, y);
        y += 7;
        doc.text(`Pending Amount: ${pendingAmount}`, 15, y);

        doc.save(`ledger-${clientDetails.name}.pdf`);
    };

    return (
        <>
        <PageLoadingArea isLoading={isLoading}>
            <div className="list-page">
                <div className="list-page-header">
                    <div className="list-page-title">
                        <h1>Ledger details</h1>
                        <p className="list-page-subtitle">
                            {clientDetails?.name
                                ? `${clientDetails.name}${clientDetails.gstNo ? ` · GST ${clientDetails.gstNo}` : ''}`
                                : 'Loading client…'}
                        </p>
                    </div>
                    <div className="list-header-actions">
                        <div className="list-search-wrap">
                            <CompanyFilterSelect
                                id="ledger-details-company-filter"
                                companies={companies}
                                selectedCompanyId={selectedCompanyId}
                                onChange={handleCompanyChange}
                            />
                        </div>
                        <div className="list-button-group">
                            <button
                                type="button"
                                className="list-secondary-button"
                                onClick={() => navigate('/ledgerClients')}
                            >
                                Back to ledgers
                            </button>
                            <button
                                type="button"
                                className="list-secondary-button"
                                onClick={openFilterModal}
                            >
                                {ledgerFilter === 'All'
                                    ? 'Filter'
                                    : `Filter: ${ledgerFilter}`}
                            </button>
                            <button
                                type="button"
                                className="list-secondary-button"
                                onClick={generatePDF}
                            >
                                Export PDF
                            </button>
                        </div>
                    </div>
                </div>

                <div className="form-page-section" style={{ marginBottom: '1.15rem' }}>
                    <div className="form-page-section-head">
                        <div>
                            <h2>Summary</h2>
                            <p className="form-page-section-hint">Bills, income, and pending balance</p>
                        </div>
                    </div>
                    <div className="form-page-totals">
                        <div className="form-page-summary">
                            <div className="form-page-summary-row">
                                <span>Total bills</span>
                                <span>{formatMoney(totalBillAmount)}</span>
                            </div>
                            <div className="form-page-summary-row">
                                <span>Total income</span>
                                <span>{formatMoney(totalIncomeAmount)}</span>
                            </div>
                        </div>
                        <div className="form-page-summary is-pending">
                            <div className="form-page-summary-row total">
                                <span>Pending</span>
                                <span>{formatMoney(pendingAmount)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="list-table-card">
                    <div className="list-table-scroll">
                        <table className="list-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Reference</th>
                                    <th className="col-num">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length === 0 ? (
                                    <tr className="list-empty-row">
                                        <td colSpan="4">
                                            <div className="list-empty-state">
                                                <h3>No ledger entries</h3>
                                                <p>
                                                    {ledgerFilter === 'All'
                                                        ? 'Bills and income for this client will appear here.'
                                                        : `No entries match “${filterLabel}”. Try another filter.`}
                                                </p>
                                                {ledgerFilter !== 'All' && (
                                                    <button
                                                        type="button"
                                                        className="list-secondary-button"
                                                        onClick={openFilterModal}
                                                    >
                                                        Change filter
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredData.map((item) => (
                                    <tr key={`${item.entryType}-${item.id}`} className="list-row" style={{ cursor: 'default' }}>
                                        <td>{formatDateDDMMYYYY(item.date)}</td>
                                        <td>
                                            <span className="list-primary-text">
                                                {item.entryType === 'Bill' ? 'Bill' : 'Income'}
                                            </span>
                                        </td>
                                        <td>
                                            {item.entryType === 'Bill'
                                                ? (item.invoiceNo || '—')
                                                : (item.description || item.type || '—')}
                                        </td>
                                        <td className="col-num">{formatMoney(getRowAmount(item))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageLoadingArea>

            {showFilterModal && (
                <div className="form-page-modal-overlay" onClick={() => setShowFilterModal(false)}>
                    <div className="form-page-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="form-page-modal-head">
                            <div>
                                <h2>Filter ledger</h2>
                                <p>Show bills, income, or a specific income type</p>
                            </div>
                            <button
                                type="button"
                                className="form-page-btn-ghost"
                                onClick={() => setShowFilterModal(false)}
                            >
                                Close
                            </button>
                        </div>

                        <div className="form-page-terms-list">
                            {FILTER_OPTIONS.map((option) => (
                                <label key={option.value} className="form-page-term-item">
                                    <input
                                        type="radio"
                                        name="ledgerFilter"
                                        checked={draftLedgerFilter === option.value}
                                        onChange={() => setDraftLedgerFilter(option.value)}
                                    />
                                    <span>{option.label}</span>
                                </label>
                            ))}
                        </div>

                        <div className="form-page-modal-foot">
                            <button
                                type="button"
                                className="form-page-btn-secondary"
                                onClick={clearFilter}
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                className="form-page-btn"
                                onClick={applyFilter}
                            >
                                Apply filter
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default LedgerDetails;
